import { Component, Input, computed, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Award, isRenderableAward } from '../../../../../core/models/editorialContent.model';

interface RenderedAward {
  id:          string;
  competition: string;
  editionYear?: number | null;
  result?:     string | null;
}

interface RenderedScore {
  id:          string;
  competition: string;
  editionYear?: number | null;
  score:       number;
  scale:       string;
}

/**
 * Verified awards.
 *
 * Two separations are enforced here, and both are semantic rather than visual:
 *
 *  - an EXTERNAL SCORE is not one of our sensory axes. It is a third party's rating on a third
 *    party's scale, so it is listed apart, always with its scale attached (`98/100`), never
 *    converted, normalised or drawn on the same 0-5 bars as the profile;
 *  - a MEDAL is not a score either, so "Double Gold" is kept literally and is never ranked
 *    against a number.
 *
 * Representation is text plus our own glyph. No competition artwork, medal image or badge is
 * ever rendered: that requires a licence, and the block is worth nothing if it needs one.
 */
@Component({
  selector: 'app-spirit-awards',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './spirit-awards.component.html',
  styleUrl: './spirit-awards.component.scss'
})
export class SpiritAwardsComponent {

  private source = signal<Award[]>([]);

  @Input({ required: true }) set awards(value: Award[] | null | undefined) {
    this.source.set(value ?? []);
  }

  /** Rows that state a result. A row that only carries a score is not a medal. */
  medals = computed<RenderedAward[]>(() =>
    this.source()
      .filter(isRenderableAward)
      .filter(award => typeof award.result === 'string' && award.result.trim().length > 0)
      .map((award, index) => ({
        id:          this.idOf(award, index),
        competition: award.competition,
        editionYear: award.editionYear,
        result:      award.result
      }))
  );

  /** Scores travel with their scale; without one the number means nothing and is not shown. */
  scores = computed<RenderedScore[]>(() =>
    this.source()
      .filter(isRenderableAward)
      .filter(award =>
        typeof award.externalScore === 'number'
        && typeof award.scoreScale === 'string'
        && award.scoreScale.trim().length > 0
      )
      .map((award, index) => ({
        id:          this.idOf(award, index) + ':score',
        competition: award.competition,
        editionYear: award.editionYear,
        score:       award.externalScore as number,
        scale:       award.scoreScale as string
      }))
  );

  private idOf(award: Award, index: number): string {
    return `${award.competition}:${award.editionYear ?? ''}:${index}`;
  }
}
